import React, { useState } from "react";
import { X, Check } from "lucide-react";
import { assignLabelsToCase } from "../services/labelService";

function LabelSelector({ isOpen, onClose, caseItem, allLabels, onLabelsAssigned, userId }) {
  // Get user-specific labels from labelsByUser mapping
  const initialLabels = caseItem.labelsByUser?.[userId] || caseItem.labels || [];
  const [selectedLabelIds, setSelectedLabelIds] = useState(initialLabels);
  const [isSaving, setIsSaving] = useState(false);

  const toggleLabel = (labelId) => {
    if (selectedLabelIds.includes(labelId)) {
      setSelectedLabelIds(selectedLabelIds.filter((id) => id !== labelId));
    } else {
      setSelectedLabelIds([...selectedLabelIds, labelId]);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await assignLabelsToCase(caseItem.collection, caseItem.id, selectedLabelIds, userId);
      onLabelsAssigned(caseItem.id, selectedLabelIds);
      onClose();
    } catch (error) {
      alert("Failed to assign categories. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1e1b4b] text-gray-300 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-yellow-500">Assign Categories</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Case Info */}
        <div className="p-4 border-b border-gray-700">
          <p className="text-sm text-gray-400">Case Number</p>
          <p className="text-white font-medium">{caseItem.caseNumber}</p>
        </div>

        {/* Label Selection */}
        <div className="p-4 max-h-[50vh] overflow-y-auto">
          {allLabels.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500">
                No categories available. Create categories first to assign them to cases.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {allLabels.map((label) => {
                const isSelected = selectedLabelIds.includes(label.id);
                return (
                  <button
                    key={label.id}
                    onClick={() => toggleLabel(label.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      isSelected
                        ? "bg-[#3d3a6a] ring-2 ring-yellow-500"
                        : "bg-[#2d2a5a] hover:bg-[#3d3a6a]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-6 h-6 rounded flex-shrink-0"
                        style={{ backgroundColor: label.color }}
                      />
                      <span className="text-sm text-white">{label.name}</span>
                    </div>
                    {isSelected && (
                      <Check size={18} className="text-yellow-500" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || allLabels.length === 0}
            className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LabelSelector;
